import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN
import { Radio, Tag, Empty, Select, Tooltip } from "antd";
// I18N
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  updateBankSelected,
  updateLiftSelected,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// FLAT BUFFERS
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
// INTERFACES
import { BankInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { RootState } from "../../../app/store";
// LOCAL
import FloorButtonGrid from "./FloorButtonGrid";
import useFrontRearDoorAvailability from "./useFrontRearDoorAvailability";
import { COMMAND_SPECS, liftHasDoor, CommandKind } from "./terminalCommandSpecs";

/** Sentinel target meaning "address the bank, not one particular car". */
export const ALL_LIFTS = "__ALL_LIFTS__";

interface Props {
  /** Bank currently selected in the Terminal filters. */
  bank: BankInterface;
  /** Edge server the bank lives behind, from the Terminal filter selection. */
  serverId?: string;
}

/**
 * Command buttons for the bank selected in the Terminal.
 *
 * Distinct from `TerminalBankCommands`, which only appears for MCE-backed
 * banks or banks with `action_bank` rows configured, and only inside the JSON
 * viewer block. This panel is unconditional: any bank a user can select in the
 * Terminal filters can be commanded from here, so pressing a button and
 * watching the resulting TX row appear in the table below is a workflow that
 * always works rather than one that depends on how the bank was provisioned.
 *
 * Sends go through `usePostCommand`, the same path the dashboard's own action
 * buttons use, so service-mode handling and WebSocket routing stay identical.
 */
export default function TerminalCommandPanel({ bank, serverId = "" }: Props) {
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const { theme } = useSelector((state: RootState) => state.home);

  const [commandKind, setCommandKind] = useState<CommandKind>("ENERGIZE_CAR_CALL");
  const [target, setTarget] = useState<string>(ALL_LIFTS);
  const { hasFront, hasRear, door, setDoor } = useFrontRearDoorAvailability(bank);
  const { triggerPostCommand, isLoadingPostCommand } = usePostCommand();

  const bankId = String(bank?.id ?? "");

  const lifts = useMemo<LiftInterface[]>(
    () => ((bank?.lifts ?? []) as LiftInterface[]).slice().sort((a, b) => a.position - b.position),
    [bank],
  );

  // Highest floor first, matching the physical arrangement of a hall fixture.
  const sortedFloors = useMemo<BankFloorInterface[]>(
    () =>
      ((bank?.bankFloors ?? []) as BankFloorInterface[])
        .slice()
        .sort((a, b) => b.floorIndex - a.floorIndex),
    [bank],
  );

  const spec = useMemo(
    () => COMMAND_SPECS.find((s) => s.key === commandKind) ?? COMMAND_SPECS[0],
    [commandKind],
  );

  // Reset the car selection when the operator switches banks — a lift id from
  // the previous bank would otherwise stay selected and address a car that is
  // not in the list any more.
  useEffect(() => {
    setTarget(ALL_LIFTS);
  }, [bankId]);

  const selectedLift = useMemo<LiftInterface | undefined>(
    () => lifts.find((lift) => String(lift.id ?? "") === target),
    [lifts, target],
  );

  /**
   * The car a command is addressed to. A per-lift command uses the explicit
   * selection; a hall call is bank-level, but the Redux command body still
   * requires a lift id, so both fall back to the first car — the same
   * convention `TerminalBankCommands` uses.
   */
  const commandLift = (): LiftInterface | undefined => selectedLift ?? lifts[0];

  const isFloorEnabled = (floor: BankFloorInterface): boolean => {
    if (spec.perLift) return liftHasDoor(commandLift(), floor, door);
    return door === "FRONT" ? !!floor.frontDoor : !!floor.rearDoor;
  };

  const sendCommand = (floor: BankFloorInterface) => {
    // Mirror the dispatch order every other command surface uses: server, then
    // bank, then lift, so `usePostCommand` reads a fully consistent selection.
    if (serverId) dispatch(updateServerId(serverId));
    dispatch(updateBankSelected(bank as never));
    const lift = commandLift();
    if (lift) dispatch(updateLiftSelected(lift as never));
    triggerPostCommand({
      floor: floor.floorIndex,
      command: spec.controlFor(door),
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
  };

  if (!bankId) {
    return (
      <div
        className={`monitoring__card__${theme} generalStyles__width100`}
        style={{ marginBottom: 16, padding: 16 }}
        data-testid="terminal-command-panel-empty"
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>Select a bank or lift above to send commands</span>}
        />
      </div>
    );
  }

  const targetOptions = [
    { value: ALL_LIFTS, label: "All lifts (bank)" },
    ...lifts.map((lift) => ({
      value: String(lift.id ?? ""),
      label: String(lift.name ?? `Car ${lift.position}`),
    })),
  ];

  return (
    <div
      className={`monitoring__card__${theme} generalStyles__width100`}
      style={{ marginBottom: 16, padding: 16 }}
      data-testid="terminal-command-panel"
    >
      <div className="generalStyles__spaceBetween" style={{ marginBottom: 12 }}>
        <Tag color="blue">Send Command</Tag>
        <span style={{ fontSize: 12, opacity: 0.7 }}>
          {String(bank?.name ?? "")} · {lifts.length} lift{lifts.length === 1 ? "" : "s"}
        </span>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <Select
          size="small"
          style={{ minWidth: 200 }}
          value={target}
          onChange={setTarget}
          options={targetOptions}
          aria-label="Command target"
          data-testid="terminal-command-target"
        />
        <Radio.Group
          value={commandKind}
          onChange={(e) => setCommandKind(e.target.value)}
          size="small"
        >
          {COMMAND_SPECS.map((s) => (
            <Radio.Button key={s.key} value={s.key}>
              {s.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Radio.Group value={door} onChange={(e) => setDoor(e.target.value)} size="small">
          <Radio.Button value="FRONT" disabled={!hasFront}>
            {t("general.frontDoors")}
          </Radio.Button>
          <Radio.Button value="REAR" disabled={!hasRear}>
            {t("general.rearDoors")}
          </Radio.Button>
        </Radio.Group>
        {!spec.perLift && (
          <Tooltip title="Hall calls address the bank, not one car">
            <Tag color="purple" style={{ marginLeft: 8 }}>
              Bank-level
            </Tag>
          </Tooltip>
        )}
      </div>

      {sortedFloors.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>{t("general.bankHasNoFrontRearDoors")}</span>}
        />
      ) : (
        <FloorButtonGrid
          floors={sortedFloors}
          isLoading={isLoadingPostCommand}
          isEnabled={isFloorEnabled}
          tooltip={(floor, enabled) =>
            enabled
              ? `${spec.label} ${door === "FRONT" ? "front" : "rear"} • ${
                  floor.name || `Floor ${floor.floorIndex}`
                }`
              : `No ${door.toLowerCase()} door on ${floor.name || `floor ${floor.floorIndex}`}`
          }
          onClick={sendCommand}
        />
      )}
    </div>
  );
}
